import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Send, Check, Loader2 } from "lucide-react";

import api from "@/app/api";
import { useUserOptional } from "@/components/auth-provider";
import { Button } from "@stockline/ui/components/button";

const PROMPT_COUNT = 5;

export function FeedbackForm({
  pageKind,
  errorMessage,
}: {
  pageKind: "404" | "error";
  errorMessage?: string;
}) {
  const { t } = useTranslation();
  const user = useUserOptional();
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">(
    "idle",
  );
  const [errorText, setErrorText] = useState<string | null>(null);

  // Pick a prompt variant once per mount.
  const promptIndex = useMemo(
    () => Math.floor(Math.random() * PROMPT_COUNT),
    [],
  );

  // Feedback is only for signed-in users — we use their identity in the
  // email and also want to avoid anonymous spam reaching the team inbox.
  // Null-check after hooks to respect rules-of-hooks.
  if (!user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (!trimmed || status === "sending") return;

    setStatus("sending");
    setErrorText(null);
    try {
      await api.errorFeedback.submit({
        message: trimmed,
        page_url: window.location.href,
        page_kind: pageKind,
        error_message: errorMessage ?? null,
        user_agent: navigator.userAgent,
      });
      setStatus("sent");
      setMessage("");
    } catch (err: unknown) {
      setStatus("error");
      setErrorText(err instanceof Error ? err.message : String(err));
    }
  };

  if (status === "sent") {
    return (
      <div className="w-full max-w-lg flex flex-col items-center gap-1 p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 dark:bg-emerald-950/30 dark:border-emerald-900/50 dark:text-emerald-300">
        <div className="inline-flex items-center gap-2 font-semibold">
          <Check className="h-4 w-4" />
          {t("errors.feedback.sentTitle", "Got it. Thanks!")}
        </div>
        <p className="text-sm text-emerald-700/80 dark:text-emerald-300/80">
          {t(
            "errors.feedback.sentBody",
            "Message delivered. We'll take a look.",
          )}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full max-w-lg flex flex-col gap-2"
    >
      <p className="text-sm text-muted-foreground">
        {t(`errors.feedback.prompts.${promptIndex}`)}
      </p>
      <div className="flex gap-2 items-stretch">
        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t("errors.feedback.placeholder", "Type your message…")}
          disabled={status === "sending"}
          maxLength={2000}
          className="flex-1 h-10 px-3 rounded-md border border-input bg-background text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        />
        <Button
          type="submit"
          variant="outline"
          disabled={!message.trim() || status === "sending"}
          className="border-emerald-600/40 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-600/60 hover:text-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-950/30"
        >
          {status === "sending" ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          {/* Wrap the label in a stable <span>: it keeps the icon's
              next-sibling reference constant when the icon toggles, and turns
              the label update into a safe textContent write. Without it,
              browser auto-translate replaces the bare text node and React's
              reconcile throws NotFoundError on insertBefore. */}
          <span>
            {status === "sending"
              ? t("errors.feedback.sending", "Sending…")
              : t("errors.feedback.send", "Send")}
          </span>
        </Button>
      </div>
      {status === "error" && (
        <p className="text-xs text-red-600">
          {t(
            "errors.feedback.errorSending",
            "Couldn't send that. Try once more?",
          )}
          {errorText ? ` (${errorText})` : ""}
        </p>
      )}
    </form>
  );
}
